import { Repeat2, Trash2 } from 'lucide-react';
import { useState } from 'react';
import { cn } from 'utils/cn';
import { Avatar } from '../Avatar';
import Button from '../Button';
import CopyButton from '../CopyButton';
import RevokeAccessModal from '../ShareModal/revoke-access-modal';
import Table from '../Table';


export type PeopleWithAccessProps = {
    people: PeopleProps[],
    onDelete: (id: string | number, role: string) => void,
    onResendInvite: (id: string | number) => void,
    container?: HTMLElement | ShadowRoot | null,
}
export type PeopleProps = {
    name: string,
    email: string,
    role: string,
    src: string,
    id: string | number,
    selected?: boolean,
    copyPeopleLink: string,
    isOwner: boolean
}
const PeopleWithAccessTable = ({ people, onDelete, onResendInvite, container }: PeopleWithAccessProps) => {
    const [openRevokeAccessModal, setOpenRevokeAccessModal] = useState(false);
    const [selectedPerson, setSelectedPerson] = useState<PeopleProps>({} as PeopleProps);

    return (
        <div className="overflow-y-auto max-h-[310px] snap-y pr-1 mb-4">
            <Table className="max-w-3xl h-full p-0">
                <Table.Header className="rounded-xl">
                    <Table.Row>
                        <Table.Head className="p-0 sticky top-0 bg-background z-10 overflow-hidden pt-0.5 ">Who</Table.Head>
                        <Table.Head className="p-0 sticky top-0 pl-1.5  bg-background z-10 overflow-hidden pt-0.5">Role</Table.Head>
                        <Table.Head className="p-0 sticky top-0 text-right  bg-background z-10 overflow-hidden pt-0.5">Actions</Table.Head>
                    </Table.Row>
                </Table.Header>
                <Table.Body>
                    {people.filter(person => person.name).map((person, index) => (
                        <Table.Row key={index} className="hover:bg-transparent snap-center snap-always" >
                            <Table.Cell className='px-0 py-3'>
                                <div className="flex items-center gap-3">
                                    <Avatar
                                        title={person.name}
                                        src={person.src}
                                        email={person.email}
                                        className='rounded-lg h-10 w-10'
                                        size="fit"
                                        id={person.id}
                                    />
                                    <div className="flex flex-col w-fit">
                                        <span className="font-medium text-textcolor text-sm truncate max-w-[150px]">{person.name}</span>
                                        <span className="text-xs text-gray-500 truncate max-w-[150px]">{person.email}</span>
                                    </div>
                                </div>
                            </Table.Cell>
                            <Table.Cell className="px-0">
                                <span className={cn(person.role === 'Pending' ? 'bg-tertiary text-white rounded-md' : '', 'capitalize', 'px-1.5 py-0.5')}>{person.role}</span>
                            </Table.Cell>
                            <Table.Cell className='p-0'>
                                <div className="flex gap-2 justify-end">
                                    {person.role === 'Pending' &&
                                        <CopyButton
                                            text={person.copyPeopleLink}
                                            variant='outline'
                                            size='sm'
                                            buttonText="Copy Link"
                                            tooltip='Copy and share the invitation link'
                                        />
                                    }
                                    {
                                        person.role === 'Pending' &&
                                        <Button size='icon' variant='outline' tooltip="Resend the invitation via email"
                                            toolTipPortal>
                                            <Repeat2 size={18} onClick={() => onResendInvite(person.id)} />
                                        </Button>
                                    }
                                    <Button
                                        size='icon'
                                        variant='destructive-outline'
                                        tooltip={person.isOwner ? "The project owner cannot be removed" : 'Remove access from this project'}
                                        className="text-destructive hover:text-white"
                                        disabled={person.isOwner}
                                        tooltipSide='bottom'
                                        tooltipAlign='end'
                                        onClick={() => {
                                            setOpenRevokeAccessModal(true)
                                            setSelectedPerson(person)
                                        }
                                        }
                                    >
                                        <Trash2 size={18} />
                                    </Button>
                                </div>
                            </Table.Cell>
                        </Table.Row>
                    ))}
                </Table.Body>
            </Table>
            <RevokeAccessModal
                openRevokeAccessModal={openRevokeAccessModal}
                setOpenRevokeAccessModal={setOpenRevokeAccessModal}
                selectedPerson={selectedPerson}
                onDelete={onDelete}
                container={container}
            />
        </div>
    );
};

export default PeopleWithAccessTable;
